import { auth, firestore } from '@/utils/firebase';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { addPermission, removeRecentlyViewed } from './userPrefs/userPrefsSlice';
import { userApi } from './user/userApi';
import msalInstance from '@/app/msal';
import { canViewGroup, queryByBrokerGroup, queryByOwnerId } from '@/utils/firebaseService';
import Cookies from 'js-cookie';

const BASE_QUERY = {
	baseUrl: '/', // Set your base URL here
	prepareHeaders: async (headers, { getState }) => {
		headers.set('Content-Type', 'application/json');
		headers.set('Accept', 'application/json');
		const state = getState();
		const email = state?.msalAccount?.account?.username;
		const msToken = state?.msalAccount?.account?.idToken;

		// Fallback to cookies if not in Redux yet
		const cookieToken = Cookies.get('token');
		const finalMsToken = msToken || cookieToken;

		if (finalMsToken && !headers.has('ms-token')) {
			headers.set('ms-token', finalMsToken);
		}

		const firebaseTokenEnabled = state?.firebase?.firebaseTokenEnabled;
		if (firebaseTokenEnabled) {
			try {
				// getIdToken() returns the cached token if still valid, otherwise refreshes it.
				const freshToken = auth.currentUser
					? await auth.currentUser.getIdToken()
					: state?.firebase?.firebaseToken;
				if (freshToken) {
					headers.set('Authorization', freshToken);
				}
			} catch (err) {
				console.error('[prepareHeaders] Failed to get Firebase ID token', err);
				const fallbackToken = state?.firebase?.firebaseToken;
				if (fallbackToken) {
					headers.set('Authorization', fallbackToken);
				}
			}
		}

		const userDoc = userApi.endpoints.getUserDetails.select({ email })(state)?.data;
		if (userDoc?.brokerGroupId && userDoc?.broker?.ledger) {
			headers.set('broker-id', userDoc.brokerGroupId);
			headers.set('broker-ledger', userDoc.broker.ledger);
			headers.set('user-id', userDoc.userId);
		}

		return headers;
	},
};

const createBaseQuery = (args) => fetchBaseQuery({ ...BASE_QUERY, ...(args ?? {}) });

export const publicBaseQuery = fetchBaseQuery({
	baseUrl: '/',
	prepareHeaders: async (headers, { getState }) => {
		headers.set('Content-Type', 'application/json');
		headers.set('Accept', 'application/json');
		const state = getState?.();
		const reduxToken = state?.msalAccount?.accessToken;
		const cookieToken = Cookies.get('token');
		const msToken =
			(typeof reduxToken === 'string' && reduxToken) ||
			(typeof cookieToken === 'string' && cookieToken) ||
			null;
		if (msToken) {
			headers.set('ms-token', msToken);
		}

		return headers;
	},
});

// Checks ownership of a client (ownerDocId) or a deal (its ownerDocId or clientOwnerDocId).
const matchClientOwner = async ({ state, type, id, dispatch }) => {
	const email =
		state?.msalAccount?.account?.username ?? msalInstance?.getActiveAccount()?.username;
	console.log('[matchClientOwner] Starting check:', {
		type,
		id,
		email: email ? 'present' : 'missing',
	});
	if (!email) {
		console.log('[matchClientOwner] No email found, returning false');
		return false;
	}
	let userDoc;
	try {
		userDoc = await dispatch(userApi.endpoints.getUserDetails.initiate({ email })).unwrap();
		console.log('[matchClientOwner] User details retrieved:', {
			userId: userDoc?.userId,
			brokerGroupId: userDoc?.brokerGroupId,
			hubspotId: userDoc?.hubspotId,
			brokerGroupArrayLength: userDoc?.brokerGroupArray?.length,
			brokerGroupArray: userDoc?.brokerGroupArray,
		});
	} catch (err) {
		console.error('[matchClientOwner] Failed to get user details:', err);
		return false;
	}
	const objectId = String(id);
	const arrayType = `${type}s`;
	const permissions = state?.userPrefs?.permissions?.[arrayType];
	console.log('[matchClientOwner] Permission check:', {
		objectId,
		arrayType,
		hasPermissionsCache: !!permissions,
		inPermissionsCache: permissions?.includes(objectId),
	});
	if (permissions?.includes(objectId)) {
		console.log('[matchClientOwner] Found in permissions cache, returning true');
		return true;
	}

	// Never query without an actual owner value: queryByOwnerId silently drops the
	// owner filter when ownerValue is falsy, which would leak access.
	if (!userDoc?.userId) return false;

	console.log('[matchClientOwner] Querying Firestore for:', type, objectId);
	let docs;
	try {
		if (type === 'clientId') {
			// objectId is the client's doc id, or a HubSpot id from an old link (like
			// getClientById).
			docs = await queryByOwnerId({
				collectionRef: collection(firestore, 'clients'),
				ownerField: 'ownerDocId',
				ownerValue: userDoc.userId,
				constraints: [
					/^\d+$/.test(objectId)
						? where('hubspotId', '==', objectId)
						: where('clientId', '==', objectId),
				],
			});
		} else {
			// The deal's own owner, or its client's owner.
			const docsByOwnerField = await Promise.all(
				['ownerDocId', 'clientOwnerDocId'].map((ownerField) =>
					queryByOwnerId({
						collectionRef: collection(firestore, 'deals'),
						ownerField,
						ownerValue: userDoc.userId,
						constraints: [
							/^\d+$/.test(objectId)
								? where('dealHubspotId', '==', objectId)
								: where('dealId', '==', objectId),
						],
					}),
				),
			);
			docs = docsByOwnerField.flat();
		}
		console.log('[matchClientOwner] Firestore query result:', {
			docCount: docs.length,
			hasDoc: docs.length > 0,
			firestoreProject: firestore.app?.options?.projectId,
			collectionName: type === 'clientId' ? 'clients' : 'deals',
		});
		if (docs.length > 0) {
			console.log('[matchClientOwner] Document data:', {
				brokerGroupId: docs[0]?.data()?.brokerGroupId,
				clientId: docs[0]?.data()?.clientId,
				hubspotId: docs[0]?.data()?.hubspotId,
				fileMigrationInProgress: docs[0]?.data()?.fileMigrationInProgress,
			});
		}
	} catch (err) {
		console.error('[matchClientOwner] Firestore query failed:', err);
		return false;
	}
	const doc = docs[0];

	// If no document found, return false
	if (!doc) {
		console.log('[matchClientOwner] No document found, returning false');
		return false;
	}

	// Check if the document's owner matches the user's hubspotId
	const isValid = docs.length > 0;
	if (dispatch && isValid) {
		console.log('[matchClientOwner] Adding permission to cache');
		dispatch(addPermission({ id: objectId, type: arrayType }));
	} else if (dispatch && !isValid && userDoc?.userId) {
		const viewType = type === 'clientId' ? 'clients' : type === 'ticketId' ? 'claims' : 'deals';
		console.log('[matchClientOwner] Removing from recently viewed:', {
			userId: userDoc.userId,
			id: objectId,
			type: viewType,
		});
		dispatch(removeRecentlyViewed({ userId: userDoc.userId, id: objectId, type: viewType }));
	}
	return isValid;
};
const matchBrokerGroup = async ({ state, type, id, dispatch }) => {
	const email =
		state?.msalAccount?.account?.username ?? msalInstance?.getActiveAccount()?.username;

	console.log('[matchBrokerGroup] Starting check:', {
		type,
		id,
		email: email ? 'present' : 'missing',
	});

	if (!email) {
		console.log('[matchBrokerGroup] No email found, returning false');
		return false;
	}

	let userDoc;
	try {
		userDoc = await dispatch(userApi.endpoints.getUserDetails.initiate({ email })).unwrap();
		console.log('[matchBrokerGroup] User details retrieved:', {
			userId: userDoc?.userId,
			brokerGroupId: userDoc?.brokerGroupId,
			hubspotId: userDoc?.hubspotId,
			brokerGroupArrayLength: userDoc?.brokerGroupArray?.length,
			brokerGroupArray: userDoc?.brokerGroupArray,
		});
	} catch (err) {
		console.error('[matchBrokerGroup] Failed to get user details:', err);
		return false;
	}

	const objectId = String(id);
	const brokerGroupArray = userDoc?.brokerGroupArray ?? [];
	const arrayType = `${type}s`;
	const permissions = state?.userPrefs?.permissions?.[arrayType];

	console.log('[matchBrokerGroup] Permission check:', {
		objectId,
		brokerGroupArray,
		arrayType,
		hasPermissionsCache: !!permissions,
		inPermissionsCache: permissions?.includes(objectId),
	});

	if (permissions?.includes(objectId)) {
		console.log('[matchBrokerGroup] Found in permissions cache, returning true');
		return true;
	}

	// Without group access (the Firestore rules deny the query), only ownership counts,
	// which matchClientOwner checks.
	if (!canViewGroup(userDoc)) {
		return false;
	}

	console.log('[matchBrokerGroup] Querying Firestore for:', type, objectId);

	let docs;
	try {
		if (type === 'clientId') {
			// objectId is the client's doc id, or a HubSpot id from an old link.
			docs = await queryByBrokerGroup({
				collectionRef: collection(firestore, 'clients'),
				brokerGroupIds: brokerGroupArray,
				constraints: [
					/^\d+$/.test(objectId)
						? where('hubspotId', '==', objectId)
						: where('clientId', '==', objectId),
				],
			});
		} else {
			docs = await queryByBrokerGroup({
				collectionRef: collection(firestore, 'deals'),
				brokerGroupIds: brokerGroupArray,
				constraints: [
					/^\d+$/.test(objectId)
						? where('dealHubspotId', '==', objectId)
						: where('dealId', '==', objectId),
				],
			});
		}

		console.log('[matchBrokerGroup] Firestore query result:', {
			docCount: docs.length,
			hasDoc: docs.length > 0,
			firestoreProject: firestore.app?.options?.projectId,
			collectionName: type === 'clientId' ? 'clients' : 'deals',
		});

		if (docs.length > 0) {
			console.log('[matchBrokerGroup] Document data:', {
				brokerGroupId: docs[0]?.data()?.brokerGroupId,
				clientId: docs[0]?.data()?.clientId,
				hubspotId: docs[0]?.data()?.hubspotId,
				fileMigrationInProgress: docs[0]?.data()?.fileMigrationInProgress,
			});
		}
	} catch (err) {
		console.error('[matchBrokerGroup] Firestore query failed:', err);
		return false;
	}

	const doc = docs[0];
	const isValid = docs.length > 0;

	if (dispatch && isValid) {
		console.log('[matchBrokerGroup] Adding permission to cache');
		dispatch(addPermission({ id: objectId, type: arrayType }));
	} else if (dispatch && !isValid && userDoc?.userId) {
		const viewType = type === 'clientId' ? 'clients' : type === 'ticketId' ? 'claims' : 'deals';
		console.log('[matchBrokerGroup] Removing from recently viewed:', {
			userId: userDoc.userId,
			id: objectId,
			type: viewType,
		});
		dispatch(removeRecentlyViewed({ userId: userDoc.userId, id: objectId, type: viewType }));
	}

	return isValid;
};

// Simple request deduplication - prevents multiple concurrent fetches
let pendingUserDataRequest = null;

const getUserData = async (dispatch, email) => {
	// If there's already a request in flight, return that promise
	if (pendingUserDataRequest) {
		console.log('[getUserData] Reusing existing pending request');
		return pendingUserDataRequest;
	}

	console.log('[getUserData] Starting new user data fetch');
	pendingUserDataRequest = dispatch(
		userApi.endpoints.getUserDetails.initiate({ email }),
	).unwrap();

	try {
		const result = await pendingUserDataRequest;
		console.log('[getUserData] User data fetched successfully');
		return result;
	} finally {
		pendingUserDataRequest = null;
	}
};

export const createAuthBaseQuery = (args) => {
	const baseQuery = createBaseQuery(args);

	return async (qArgs, api, extraOptions) => {
		const requestArgs = typeof qArgs === 'string' ? { url: qArgs } : qArgs;

		const state = api?.getState();
		const dispatch = api?.dispatch;
		const authArgs = {
			...(requestArgs?.params ?? {}),
			...(requestArgs?.body ?? {}),
		};

		console.log('[authBaseQuery] Checking request:', {
			url: requestArgs?.url,
			method: requestArgs?.method,
			hasBody: !!requestArgs?.body,
			authArgs,
			dealId: authArgs.dealId,
			clientId: authArgs.clientId,
			ticketId: authArgs.ticketId,
		});

		const email =
			state?.msalAccount?.account?.username ?? msalInstance?.getActiveAccount()?.username;
		if (email) {
			const userQueryKey = `getUserDetails({"email":"${email}"})`;
			const existingUser = state?.userApi?.queries[userQueryKey]?.data;

			if (!existingUser?.userId) {
				console.log('[authBaseQuery] User data not ready, fetching before validation...');
				try {
					await getUserData(dispatch, email);
					console.log('[authBaseQuery] User data is now ready');
				} catch (err) {
					console.error('[authBaseQuery] Failed to load user data:', err);
					return {
						error: {
							status: 403,
							data: JSON.stringify({
								status: 'rejected',
								reason: 'USER_DATA_UNAVAILABLE',
							}),
						},
					};
				}
			} else {
				console.log('[authBaseQuery] User data already available in state');
			}
		} else {
			console.warn('[authBaseQuery] No email found, cannot validate user');
			return {
				error: {
					status: 401,
					data: JSON.stringify({
						status: 'rejected',
						reason: 'NO_USER_EMAIL',
					}),
				},
			};
		}

		for (const type of ['clientId', 'dealId', 'ticketId']) {
			if (authArgs[type]) {
				const id = String(authArgs[type]);
				console.log(`[authBaseQuery] Validating ${type}:`, id);
				const currentState = api?.getState();
				const isValidOwner = await matchClientOwner({
					state: currentState,
					type,
					id,
					dispatch,
				});
				const isValidBrokerGroup = await matchBrokerGroup({
					state: currentState,
					type,
					id,
					dispatch,
				});

				const hasAccess = isValidBrokerGroup || isValidOwner;
				console.log(`[authBaseQuery] ${type} validation result:`, {
					isValidBrokerGroup,
					isValidOwner,
					hasAccess,
				});

				if (!hasAccess) {
					console.log(`[authBaseQuery] BLOCKED REQUEST for ${type}:`, id);
					return {
						error: {
							status: 403,
							data: JSON.stringify({
								status: 'rejected',
								reason: 'USER_FORBIDDEN',
							}),
						},
					};
				}
			}
		}

		return baseQuery(requestArgs, api, extraOptions);
	};
};

export const authBaseQuery = createAuthBaseQuery();

export default BASE_QUERY;
